'use client';

/**
 * Hook centralizzato per la lista dei boat_types.
 * Fonte unica: GET /api/boat-types (DB-driven, gestito da admin via BoatTypesTab).
 *
 * Sostituisce il vecchio array hardcoded `BOAT_TYPES` in adminTypes.ts che era
 * duplicato e con slug diversi (sailboat/motorboat/... vs vela/motoscafo/...).
 *
 * Cache module-level per evitare N fetch ad ogni montaggio: la lista è statica
 * (cambia solo quando admin modifica boat_types), ok cachare.
 */
import { useEffect, useState } from 'react';
import { listBoatTypes, type BoatType } from '@/services/boatTypesService';

let cachedTypes: BoatType[] | null = null;
let inflight: Promise<BoatType[]> | null = null;

export function useBoatTypes() {
  const [types, setTypes] = useState<BoatType[]>(cachedTypes ?? []);
  const [loading, setLoading] = useState<boolean>(cachedTypes === null);

  useEffect(() => {
    if (cachedTypes) return;
    if (!inflight) {
      inflight = listBoatTypes()
        .then((data) => {
          cachedTypes = data;
          return data;
        })
        .catch(() => {
          cachedTypes = [];
          return [] as BoatType[];
        });
    }
    inflight.then((data) => {
      setTypes(data);
      setLoading(false);
    });
  }, []);

  /** Trova label da slug; fallback al slug raw se mancante. */
  const getLabel = (slug?: string | null): string => {
    if (!slug) return '';
    return types.find((t) => t.slug === slug)?.label || slug;
  };

  /** Opzioni per <select> dropdown (active only, sort_order già applicato dal backend). */
  const options = types
    .filter((t) => t.is_active !== false)
    .map((t) => ({ value: t.slug, label: t.label }));

  return { types, options, loading, getLabel };
}
